import './styles.scss'
import React, { useEffect, useState } from 'react'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { InputSwitch } from 'primereact/inputswitch'
import { InputText } from 'primereact/inputtext'
import { useTranslation } from 'react-i18next'
import { supabase } from 'Services/supabaseClient'
import { getLocalUserDetails } from 'Utils/LocalStorageHandler'
import { commonMethods } from 'Utils/commonMethods'
import { TABLE_CONST } from 'Utils/Constants'
import { DialogModal } from 'Components/UIComponents'
import Loader from 'Components/Loader'

const UserCommunities = ({ allUserData }) => {
  const { t } = useTranslation()
  const userDetails = getLocalUserDetails()

  const [windowSize, setWindowSize] = useState(window.innerWidth)
  const [searchTerm, setSearchTerm] = useState('')
  const [userCommunities, setUserCommunities] = useState([])
  const [userAllCommunitiesData, setUserAllCommunitiesData] = useState([])
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onDismiss: () => {}, onRetry: () => {}, onHide: () => {}, onSuccess: () => {}, onConfirmation: () => {} })

  const rowCount = windowSize < TABLE_CONST.maxWidthTab ? TABLE_CONST.mobileRowCount : 5
  const paginationButtons = windowSize < TABLE_CONST.maxWidthTab ? TABLE_CONST.mobilePaginationButtonCount : TABLE_CONST.paginationButtonCount

  useEffect(() => {
    getUserCommunityData()

    const handleWindowResize = () => {
      setWindowSize(window.innerWidth)
    }

    window.addEventListener('resize', handleWindowResize)

    return () => window.removeEventListener('resize', handleWindowResize)
  }, [])

  const getUserCommunityData = async () => {
    try {
      // fetch membership rows for this user (include per-user is_internal and joined_at)
      const { data: memberRows, error: memberErr } = await supabase.from('community_members').select('community_id,joined_at,is_internal, blocked').eq('userid', allUserData.userid)

      if (memberErr) {
        return
      }

      const ids = (memberRows || []).map((item) => item.community_id).filter(Boolean)

      if (!ids.length) {
        setUserCommunities([])

        return
      }

      const joinedAtMap = {}
      const memberInternalMap = {}
      const memberBlockedMap = {}

      memberRows.forEach((m) => {
        joinedAtMap[m.community_id] = m.joined_at
        memberBlockedMap[m.community_id] = m.blocked
        if (typeof m.is_internal !== 'undefined' && m.is_internal !== null) memberInternalMap[m.community_id] = m.is_internal
      })

      const { data: communities, error: commErr } = await supabase.from('communities').select('id, name, is_internal, created_at, required_subscription').in('id', ids)

      if (commErr) {
        return
      }

      const users = (communities || []).map((c) => {
        const membership = (memberRows || []).find((m) => m.community_id === c.id) || {}

        return {
          userId: c.id,
          communityName: c.name,
          internalUser: c.is_internal,
          date: commonMethods.utcToLocalTime(joinedAtMap[c.id] || c.created_at),
          subscription: c.required_subscription,
          isUserBlocked: !!membership.blocked
        }
      })

      setUserCommunities(users)
      setUserAllCommunitiesData(users)
    } catch (err) {
      setShowDialog((prev) => ({
        ...prev,
        showModal: true,
        modalType: 'error',
        message: t('SORRY_COULD_NOT_FETCH_THE_DATA_PLEASE_TRY_AGAIN_LATER'),
        onDismiss: handleDismiss,
        onHide: handleDismiss
      }))
    }
  }

  const updateUserBlockedState = (rowData, blocked) => {
    setUserCommunities((prev = []) => prev.map((u) => (u.userId === rowData.userId ? { ...u, isUserBlocked: !!blocked } : u)))
    setUserAllCommunitiesData((prev = []) => prev.map((u) => (u.userId === rowData.userId ? { ...u, isUserBlocked: !!blocked } : u)))
  }

  const confirmToggleForBlock = (rowData) => {
    setShowDialog((prev) => ({
      ...prev,
      showModal: true,
      modalType: 'info',
      message: rowData?.isUserBlocked ? t(`Are You Sure, You Want To Unblock ${allUserData.first_name} From ${rowData.communityName}?`) : t(`Are You Sure, You Want To Block ${allUserData.first_name} From ${rowData.communityName}?`),
      onHide: handleDismiss,
      onConfirmation: rowData?.isUserBlocked ? () => unblockUserFromCommunity(rowData) : () => blockUserFromCommunity(rowData)
    }))
  }

  const blockUserFromCommunity = (rowData) => handleUserBlockAction(rowData, 'block')

  const unblockUserFromCommunity = (rowData) => handleUserBlockAction(rowData, 'unblock')

  const handleUserBlockAction = async (rowData, action) => {
    setShowDialog((prev) => ({ ...prev, showModal: false }))

    const isBlockAction = action === 'block'
    const rpcName = isBlockAction ? 'block_user_from_community' : 'unblock_user_from_community'
    const successMessage = isBlockAction ? `${allUserData?.first_name} Has Been Blocked From ${rowData?.communityName} Successfully` : `${allUserData?.first_name} Has Been Unblocked From ${rowData?.communityName} Successfully`
    const errorMessage = isBlockAction ? t('Failed to block user from community') : t('Failed to unblock user from community')
    const reason = isBlockAction ? 'Blocked via admin console' : 'Unblocked via admin console'

    try {
      const params = {
        target_userid: allUserData?.userid,
        community_id: rowData.userId,
        bot_user_id: userDetails?.id,
        reason
      }

      const { data, error } = await supabase.rpc(rpcName, params)

      if (error) throw error

      updateUserBlockedState(rowData, isBlockAction)

      setShowDialog((prev) => ({
        ...prev,
        showModal: true,
        modalType: 'success',
        message: successMessage,
        onDismiss: handleDismiss,
        onHide: handleDismiss
      }))

      if (data && data.success === false) {
        updateUserBlockedState(rowData, !isBlockAction)

        setShowDialog((prev) => ({
          ...prev,
          showModal: true,
          modalType: 'error',
          message: data.error || errorMessage,
          onDismiss: handleDismiss,
          onHide: handleDismiss,
          onRetry: () => handleUserBlockAction(rowData, action)
        }))
      }
    } catch (err) {
      setShowDialog((prev) => ({
        ...prev,
        showModal: true,
        modalType: 'error',
        message: errorMessage,
        onDismiss: handleDismiss,
        onHide: handleDismiss
      }))
    }
  }

  const deleteMessages = async (rowData) => {
    try {
      const payload = { deleted_at: new Date().toISOString() }

      const { data, error } = await supabase.from('messages').update(payload).eq('userid', allUserData?.userid).eq('community_id', rowData.userId)

      if (error) {
        throw error
      }

      setShowDialog((prev) => ({
        ...prev,
        showModal: true,
        modalType: 'success',
        message: t(`Messages Deleted Successfully For ${allUserData?.first_name} In ${rowData.communityName}`),
        onDismiss: handleDismiss,
        onHide: handleDismiss
      }))
    } catch (err) {
      setShowDialog((prev) => ({
        ...prev,
        showModal: true,
        modalType: 'error',
        message: t('Failed To Mark Messages As Deleted For', rowData.communityName),
        onDismiss: handleDismiss,
        onHide: handleDismiss
      }))
    }
  }

  const handleSearchChange = (e) => {
    const raw = e.target?.value || ''
    const q = raw.trim().toLowerCase()

    setSearchTerm(raw)

    if (!q) {
      // restore original list
      setUserCommunities(userAllCommunitiesData || [])

      return
    }

    const filtered = (userAllCommunitiesData || []).filter((user) => {
      const name = (user?.communityName || '').toString().toLowerCase()
      const id = (user?.userId || '').toString().toLowerCase()
      const date = (user?.date || '').toString().toLowerCase()

      return name.includes(q) || id.includes(q) || date.includes(q)
    })

    setUserCommunities(filtered)
  }

  const handleDeleteMessages = (rowData) => {
    setShowDialog((prev) => ({
      ...prev,
      showModal: true,
      modalType: 'info',
      message: t(`Are You Sure, You Want To Delete Message For ${rowData.communityName} ? `),
      onDismiss: handleDismiss,
      onHide: handleDismiss,
      onConfirmation: () => deleteMessages(rowData)
    }))
  }

  const handleDismiss = () => {
    setShowDialog((prev) => ({ ...prev, showModal: false }))
  }

  const actionTemplate = (rowData) => (
    <div className="action-container">
      <Button type="button" icon="pi pi-trash" className={'delete-icon'} tooltip={t('Delete All Messages')} tooltipOptions={{ position: 'top' }} onClick={() => handleDeleteMessages(rowData)} />
    </div>
  )

  const columns = [
    { dataField: 'userId', text: t('Id'), className: 'id-column', isSortable: false },
    { dataField: 'communityName', text: t('Community Name'), className: 'name-column', isSortable: true },
    { dataField: 'date', text: t('Joined Date'), className: 'join-date-column', isSortable: true },
    { dataField: 'internalUser', text: t('Community Type'), className: 'community-type-column', isSortable: false, body: (row) => (row?.internalUser ? t('Internal') : t('General')) },
    {
      dataField: 'isUserBlocked',
      text: t('Block User'),
      className: 'block-user-column',
      isSortable: false,
      body: (rowData) => <InputSwitch checked={!!rowData?.isUserBlocked} onChange={() => confirmToggleForBlock(rowData)} disabled={!!allUserData?.is_internal_user} />
    },
    { body: actionTemplate, text: t('ACTIONS'), className: 'action-column' }
  ]

  return (
    <div>
      <div className="user-community-div">
        <div className="search-div p-input-icon-left">
          <i className="pi pi-search" data-testid="search-icon" />
          <InputText value={searchTerm} placeholder={t('SEARCH')} onChange={handleSearchChange} />
        </div>
        <div className="table-div">
          <DataTable value={userCommunities} removableSort paginator rows={rowCount} pageLinkSize={paginationButtons} scrollHeight="550px" scrollable>
            {columns.map((col, i) => (
              <Column key={i} field={col.dataField} sortable={col.isSortable} header={col.text} body={col.body} className={col.className} />
            ))}
          </DataTable>
        </div>
      </div>

      <DialogModal {...showDialog} onSuccess={handleDismiss} onDismiss={handleDismiss} onHide={handleDismiss} />
    </div>
  )
}

export default Loader(UserCommunities)
